import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  LuBriefcase,
  LuCalendarClock,
  LuCalendarDays,
  LuCalendarX,
  LuChevronDown,
  LuChevronUp,
  LuCircleCheck,
  LuClock,
  LuCopy,
  LuFolderKanban,
  LuFolderOpen,
  LuIdCard,
  LuLoaderCircle,
  LuMail,
  LuMapPin,
  LuPhone,
  LuUserCheck,
  LuUserCog,
  LuUserPen,
  LuUserX,
  LuX,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import GlassIcon from '../../../components/ui/GlassIcon';
import IconButton from '../../../components/ui/IconButton';
import { getInitials } from '../../../components/ui/Avatar';
import { GLASS_CHIP, GLASS_PRESSABLE } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';
import { PRIMARY_ADDRESS_WORK, getPrimaryAddress } from '../../../utils/employeeAddress';
import { displayValue, formatDate, formatIsoDate, getFilterValueLabel, isEmptyValue } from '../employeeColumns';

const TINTS = {
  teal: 'tw-bg-teal-500/10 tw-text-teal-600 dark:tw-bg-teal-400/10 dark:tw-text-teal-300',
  blue: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-bg-blue-400/10 dark:tw-text-blue-300',
  violet: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-bg-violet-400/10 dark:tw-text-violet-300',
  indigo: 'tw-bg-indigo-500/10 tw-text-indigo-600 dark:tw-bg-indigo-400/10 dark:tw-text-indigo-300',
  amber: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-bg-amber-400/10 dark:tw-text-amber-300',
  emerald: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-bg-emerald-400/10 dark:tw-text-emerald-300',
};

const NAV_BUTTON = 'disabled:tw-pointer-events-none disabled:tw-opacity-40';

const DetailRow = ({ icon: Icon, label, value, href, copyable }) => {
  const { t } = useTranslation();
  const empty = isEmptyValue(value) || value === '-';
  const text = empty ? '-' : String(value);
  const valueClass = `tw-block tw-truncate tw-text-sm tw-font-medium ${empty ? 'tw-text-slate-400 dark:tw-text-slate-500' : 'tw-text-slate-800 dark:tw-text-slate-100'}`;

  return (
    <li className="tw-group tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-px-2 tw-py-1.5 tw-transition-colors hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]">
      <span className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-slate-900/[0.04] tw-text-slate-400 dark:tw-bg-white/[0.06] dark:tw-text-slate-500">
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{label}</p>
        {href && !empty ? (
          <a href={href} title={text} className={`${valueClass} tw-transition-colors hover:tw-text-teal-700 dark:hover:tw-text-teal-300`}>{text}</a>
        ) : (
          <span title={empty ? undefined : text} className={valueClass}>{text}</span>
        )}
      </div>
      {copyable && !empty && (
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              toast.success(t('employees.panel.copied', { label }));
            } catch (error) {
              toast.error(t('employees.panel.copyFailed'));
            }
          }}
          aria-label={t('employees.panel.copyLabel', { label })}
          title={t('employees.panel.copy')}
          className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-opacity-0 tw-transition hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 focus-visible:tw-opacity-100 group-hover:tw-opacity-100 [@media(hover:none)]:tw-opacity-100 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
        >
          <LuCopy aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        </button>
      )}
    </li>
  );
};

DetailRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  href: PropTypes.string,
  copyable: PropTypes.bool,
};

/**
 * Summary and actions of the selected employee: identity, every action of the
 * old action bar, key details (copyable) and the status switch. ↑/↓ step to
 * the previous/next employee of the list.
 */
const EmployeeDetailPanel = forwardRef(({
  employee,
  position,
  pendingLeaveCount,
  hasUser,
  isCheckingUser,
  canOpenFiles,
  onPrev,
  onNext,
  onClose,
  onOpenCard,
  onEdit,
  onToggleStatus,
  onOpenFiles,
  onOpenProjects,
  onCreateUser,
  className = '',
}, closeButtonRef) => {
  const { t, locale } = useTranslation();
  const isActive = employee.Active === 1;
  const fullName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim() || '-';
  const address = getPrimaryAddress(employee);
  const phone = isEmptyValue(employee.phone) ? '' : String(employee.phone);
  const email = isEmptyValue(employee.eMail) ? '' : String(employee.eMail);

  let accountLabel = t('employees.panel.accountCreate');
  let accountTitle = t('employees.panel.accountCreateHint');
  let AccountIcon = LuUserCog;
  if (isCheckingUser) {
    accountLabel = t('employees.panel.accountChecking');
    accountTitle = t('employees.panel.accountCheckingHint');
    AccountIcon = LuLoaderCircle;
  } else if (hasUser) {
    accountLabel = t('employees.panel.accountExists');
    accountTitle = t('employees.panel.accountExistsHint');
    AccountIcon = LuCircleCheck;
  }

  const actions = [
    { key: 'card', label: t('employees.panel.card'), icon: LuIdCard, tint: 'teal', onClick: onOpenCard },
    { key: 'edit', label: t('employees.panel.edit'), icon: LuUserPen, tint: 'blue', onClick: onEdit },
    canOpenFiles && { key: 'files', label: t('employees.panel.files'), icon: LuFolderOpen, tint: 'violet', onClick: onOpenFiles },
    { key: 'projects', label: t('employees.panel.projects'), icon: LuFolderKanban, tint: 'indigo', onClick: onOpenProjects },
    {
      key: 'account',
      label: accountLabel,
      title: accountTitle,
      icon: AccountIcon,
      tint: hasUser && !isCheckingUser ? 'emerald' : 'amber',
      onClick: onCreateUser,
      disabled: isCheckingUser || hasUser,
      spin: isCheckingUser,
    },
  ].filter(Boolean);

  const details = [
    { key: 'tc', icon: LuIdCard, label: t('employees.columns.tcNumber'), value: employee.tcNumber, copyable: true },
    { key: 'phone', icon: LuPhone, label: t('employees.columns.phone'), value: phone, href: phone ? `tel:${phone.replace(/\s+/g, '')}` : undefined, copyable: true },
    { key: 'mail', icon: LuMail, label: t('employees.columns.eMail'), value: email, href: email ? `mailto:${email}` : undefined, copyable: true },
    { key: 'profession', icon: LuBriefcase, label: t('employees.columns.profession'), value: employee.profession },
    { key: 'workType', icon: LuClock, label: t('employees.columns.workType'), value: isEmptyValue(employee.workType) ? '' : getFilterValueLabel(t, 'workType', employee.workType) },
    { key: 'start', icon: LuCalendarDays, label: t('employees.columns.startDate'), value: formatIsoDate(employee.startDate, locale) },
    !isActive && { key: 'exit', icon: LuCalendarX, label: t('employees.columns.employeeExitDate'), value: formatDate(employee.employeeExitDate, locale) },
    {
      key: 'address',
      icon: LuMapPin,
      label: t('employees.panel.primaryAddress', { label: t(address.key === PRIMARY_ADDRESS_WORK ? 'employees.columns.workAddress' : 'employees.columns.address') }),
      value: address.value,
    },
  ].filter(Boolean);

  return (
    <div className={`tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden tw-rounded-[1.75rem] ${className}`}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-pt-4">
        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 tw-tabular-nums dark:tw-text-slate-500">
          {t('employees.panel.heading')}{position ? ` · ${position.index + 1} / ${position.total}` : ''}
        </p>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <IconButton label={t('employees.panel.previous')} onClick={onPrev} disabled={!onPrev} className={NAV_BUTTON}>
            <LuChevronUp aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
          <IconButton label={t('employees.panel.next')} onClick={onNext} disabled={!onNext} className={NAV_BUTTON}>
            <LuChevronDown aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
          <IconButton ref={closeButtonRef} label={t('employees.panel.close')} onClick={onClose}>
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
        </div>
      </div>

      <motion.div
        key={employee.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18 }}
        className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
      >
        <div className="tw-flex tw-flex-col tw-items-center tw-px-5 tw-pb-4 tw-pt-1 tw-text-center">
          <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="avatarXl">
            {getInitials(employee.eName, employee.eSurname)}
          </GlassIcon>
          <h2 className="tw-mt-3 tw-max-w-full tw-truncate tw-text-lg tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{fullName}</h2>
          {!isEmptyValue(employee.departman) && (
            <p className="tw-mt-1 tw-max-w-full tw-truncate tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{displayValue(employee.departman)}</p>
          )}
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-justify-center tw-gap-1.5">
            <Badge tone={isActive ? 'success' : 'neutral'}>
              <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${isActive ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
              {isActive ? t('employees.status.active') : t('employees.status.inactive')}
            </Badge>
            {pendingLeaveCount > 0 && (
              <Badge tone="warning">
                <LuCalendarClock aria-hidden="true" className="tw-h-3 tw-w-3" />
                {t('employees.panel.pendingLeaves', { count: pendingLeaveCount })}
              </Badge>
            )}
          </div>
        </div>

        <div className="tw-min-h-0 tw-flex-1 tw-space-y-5 tw-overflow-y-auto tw-px-3 tw-pb-4 glass-scrollbar">
          <section aria-label={t('employees.panel.actions')} className="tw-grid tw-grid-cols-2 tw-gap-2 tw-px-1">
            {actions.map((action, index) => {
              const Icon = action.icon;
              const spansRow = index === actions.length - 1 && actions.length % 2 === 1;
              return (
                <button
                  key={action.key}
                  type="button"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  title={action.title}
                  className={`tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-rounded-2xl tw-p-2.5 tw-text-left tw-text-sm tw-font-semibold tw-text-slate-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-70 disabled:active:tw-scale-100 dark:tw-text-slate-200 ${GLASS_CHIP} ${GLASS_PRESSABLE} ${spansRow ? 'tw-col-span-2' : ''}`}
                >
                  <span className={`tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-xl ${TINTS[action.tint]}`}>
                    <Icon aria-hidden="true" className={`tw-h-4 tw-w-4 ${action.spin ? 'tw-animate-spin' : ''}`} />
                  </span>
                  <span className="tw-min-w-0 tw-leading-tight">{action.label}</span>
                </button>
              );
            })}
          </section>

          <section aria-labelledby="employee-detail-info">
            <h3 id="employee-detail-info" className="tw-px-2 tw-pb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
              {t('employees.panel.details')}
            </h3>
            <ul>
              {details.map(({ key, ...detail }) => <DetailRow key={key} {...detail} />)}
            </ul>
          </section>
        </div>
      </motion.div>

      <div className="tw-border-t tw-border-slate-200/70 tw-p-3 dark:tw-border-white/10">
        <button
          type="button"
          onClick={onToggleStatus}
          title={t('employees.panel.statusHint')}
          className={`tw-flex tw-h-11 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-text-sm tw-font-semibold tw-transition-[background-color,transform] tw-duration-200 active:tw-scale-[0.98] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
            isActive
              ? 'tw-border-rose-500/25 tw-bg-rose-500/10 tw-text-rose-700 hover:tw-bg-rose-500/15 dark:tw-border-rose-400/20 dark:tw-text-rose-300'
              : 'tw-border-emerald-500/25 tw-bg-emerald-500/10 tw-text-emerald-700 hover:tw-bg-emerald-500/15 dark:tw-border-emerald-400/20 dark:tw-text-emerald-300'
          }`}
        >
          {isActive ? <LuUserX aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuUserCheck aria-hidden="true" className="tw-h-4 tw-w-4" />}
          {isActive ? t('employees.panel.deactivate') : t('employees.panel.activate')}
        </button>
      </div>
    </div>
  );
});

EmployeeDetailPanel.displayName = 'EmployeeDetailPanel';

EmployeeDetailPanel.propTypes = {
  employee: PropTypes.object.isRequired,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  pendingLeaveCount: PropTypes.number,
  hasUser: PropTypes.bool,
  isCheckingUser: PropTypes.bool,
  canOpenFiles: PropTypes.bool,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onOpenCard: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onOpenFiles: PropTypes.func.isRequired,
  onOpenProjects: PropTypes.func.isRequired,
  onCreateUser: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default EmployeeDetailPanel;
